import { call, put, all, takeEvery, select, take } from "redux-saga/effects";

import {
    EAuthActionTypes,
    IAuthSignInActionPayload,
    IAuthSignUpActionPayload,
    TStore,
    EAppActionTypes,
    ICommunityInvitation,
    ECommunitiesActionTypes,
    ESubscriptionActionTypes,
} from "models";
import {
	api,
	notify,
	setAuthenticationLoading,
	setUser,
	appInit,
	clearToken,
	resetStore,
	resetStoreSuccess,
	setAppLoading,
	getCommunitiesJoined,
	setInvite,
	getEditProfile,
	storeLogout,
	authenticateByToken,
	getUserCurrentSubscriptionAction,
	joinCommunity,
	getInvitationDataByCommunityLink,
	history,
	verifyEmailCompleted,
	verifyEmailFailed,
	resendVerificationEmailCompleted,
	resendVerificationEmailFailed,
	checkEmailVerificationStatusCompleted,
	requestMagicLinkCompleted,
	requestMagicLinkFailed,
	verifyMagicLinkCompleted,
	verifyMagicLinkFailed,
} from "store";

import { updateToken } from "../..";
import { getInvite, getSubscriptionManagementActionSaga, requestMagicLink, verifyMagicLink } from "store/actions";
import { isItAWhiteLabel, LocalStorageKeyCustomToken, CustomTokenPassword } from "utils";
import { whiteLabelConfig } from "config";
import { rollbar } from "utils";
import { AES } from "crypto-js";
import { SSO_ENABLED, WHITELABEL_LOGIN_PAGE } from "config_environment";

const getOneSignalId = (state: TStore) => state.oneSignal.id;
const getUser = (state: TStore) =>
    state.auth.credentials && state.auth.credentials.user;
const getUserId = (state: TStore) => state.auth.credentials?.user.id;
const getCommunityInvitation = (state: TStore) =>
    state.auth.communityInvitation;
const whitelist = [
    "/auth/sign-up",
    "/auth/sign-in",
    "/invites/",
    "/hubs/",
    "/auth/forgot-password",
    "/reset-password",
    "/auth/token/",
];

function* authByTokenHandle({
    payload,
}: ReturnType<typeof authenticateByToken>) {
    try {
        const { data } = yield call(api.auth.authenticateByToken, payload);
        if (!data.user.access_token || !data.user) {
            yield call(
                notify,
                "error",
                "Authentication Failed",
                "Invalid credentials... please try again",
            );
            yield put(setAppLoading(false));
        } else {
            yield call(
                notify,
                "success",
                "Authentication Success",
                "Welcome back",
            );
            updateToken(data.user.access_token);
            yield put(getCommunitiesJoined());

            yield put(
                setUser({
                    credentials: data,
                }),
            );
            let communityId: number = 0;
            let communityData: Record<string, any> | null = null;
            if (data.user) {
                communityId =
                    data.user.last_visited_community_id ||
                    data.user.joined_community_id ||
                    data.user.last_previewed_community_id;
                if (communityId) {
                    const { data } = yield call(api.community.home, {
                        community_id: communityId,
                    });
                    communityData = data;
                }

                if (
                    communityData &&
                    (communityData?.meta?.privacy === "open" ||
                        communityData?.meta?.is_approved)
                ) {
                    yield put(
                        getEditProfile({ communityId, userId: data.user.id }),
                    );
                    yield put(
                        getUserCurrentSubscriptionAction({
                            communityId,
                            userId: data.user.id,
                        }),
                    );
                    yield put(
                        appInit({
                            userId: data.user.id,
                            communityId: communityId,
                            token: data.user.access_token,
                        }),
                    );
                    yield put(
                        getSubscriptionManagementActionSaga({
                            communityId,
                        }),
                    );
                    yield take(
                        ESubscriptionActionTypes.COMPLETE_GET_SUBSCRIPTION_MANAGEMENT,
                    );
                }
            } else {
                throw Error("Error: The request could not be performed.");
            }
        }
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
        yield call(
            notify,
            "error",
            "Authentication Failed",
            "Invalid credentials... please try again",
        );
        yield put(setAppLoading(false));
    }
}

function* loginHandle({ payload }: IAuthSignInActionPayload) {
    yield put(setAppLoading(true));
    let response;
    try {
        const oneSignalId = yield select(getOneSignalId);
        response = yield call(api.auth.login, {
            ...payload,
            player_id: oneSignalId,
        });
        if (!response.data.user.access_token || !response.data.user) {
            yield call(
                notify,
                "error",
                "Authentication Failed",
                "Invalid credentials... please try again",
            );
            yield put(setAppLoading(false));
        } else {
            yield call(
                notify,
                "success",
                "Authentication Success",
                "Welcome back",
            );
            
            console.log(response.data)

            updateToken(response.data.user.access_token);
            yield put(
				setUser({
					credentials: response.data,
				}),
			);

            // EMAIL VERIFICATION DISABLED - Always treat as verified
            // const isVerified = response.data.user.email_verified === true;
            const isVerified = true;

            // Update the verification status in Redux
            yield put(checkEmailVerificationStatusCompleted({ isVerified }));

            // EMAIL VERIFICATION DISABLED - Skip verification redirect
            // if(!isVerified){
            //     updateToken(response.data.user.access_token);
            //     yield put(setUser({ credentials: response.data }));
            //     const customTokenValEncrypted = AES.encrypt(
            //         `${response.data.user.id}  ${response.data.user.access_token}`,
            //         CustomTokenPassword,
            //     );
            //     localStorage.setItem(LocalStorageKeyCustomToken, customTokenValEncrypted.toString());
            //     yield history.push("/verify-email");
            //     yield put(setAppLoading(false));
            //     return;
            // }
            
			
            yield put(getCommunitiesJoined());

			let communityId: number = 0;
			if (response.data.user) {
				communityId =
					response.data.user.last_visited_community_id ||
					response.data.user.joined_community_id ||
					response.data.user.last_previewed_community_id;
			}

            if (communityId) {
                const { data: communityData } = yield call(api.community.home, {
                    community_id: communityId,
                });
                if (
                    communityData.meta.privacy === "open" ||
                    communityData.meta.is_approved
                ) {
                    // alert("verify-email passed");
                    // TODO: move setUser in a getUser function (saga function)
                    const { data: editProfileData } = yield call(
                        api.profile.getEditProfile,
                        {
                            user_id: response.data.user.id,
                            community_id: communityId,
                        },
                    );
                    const userData = {
                        user: {
                            ...editProfileData.user,
                            ...response.data.user,
                            community_id: communityId,
                            joined_community_id: communityId,
                        },
                    };

                    yield put(
                        setUser({
                            credentials: userData,
                        }),
                    );
                    yield put(
                        getUserCurrentSubscriptionAction({
                            communityId,
                            userId: response.data.user.id,
                        }),
                    );
                    yield put(
                        getEditProfile({
                            communityId,
                            userId: response.data.user.id,
                        }),
                    );
                    yield put(
                        appInit({
                            userId: response.data.user.id,
                            communityId: communityId,
                            token: response.data.user.access_token,
                        }),
                    );
                    yield put(
                        getSubscriptionManagementActionSaga({
                            communityId,
                        }),
                    );
                    yield take(
                        ESubscriptionActionTypes.COMPLETE_GET_SUBSCRIPTION_MANAGEMENT,
                    );

                
                } else {
                    // EMAIL VERIFICATION DISABLED - Skip verification check
                    // if (!isVerified) {
                    //     updateToken(response.data.user.access_token);
                    //     yield put(setUser({ credentials: response.data }));
                    //     const customTokenValEncrypted = AES.encrypt(
                    //         `${response.data.user.id}  ${response.data.user.access_token}`,
                    //         CustomTokenPassword,
                    //     );
                    //     localStorage.setItem(LocalStorageKeyCustomToken, customTokenValEncrypted.toString());
                    //     yield history.push("/verify-email");
                    //     yield put(setAppLoading(false));
                    //     return;
                    // }

                    yield put(
                        appInit({
                            userId: response.data.user.id,
                            communityId: "",
                            token: response.data.user.access_token,
                        }),
                    );
                    if (!isItAWhiteLabel()) {
                        yield history.push("/communities");
                    } else {
                        yield put(
                            joinCommunity({
                                id: whiteLabelConfig?.community?.id!,
                            }),
                        );
                        yield take(
                            ECommunitiesActionTypes.joinCommunityCompleted,
                        );
                    }
                }
                yield put(setAppLoading(false));
            } else {
                throw Error("Error: The request could not be performed.");
            }
        }
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
        if (error?.response?.data?.meta) {
            if (
                Object.keys(error.response.data.meta).indexOf("is_approved") !==
                -1 &&
                !error.response.data.meta.is_approved
            ) {
                updateToken(response.data.user.access_token);

                yield put(
                    setUser({
                        credentials: response.data,
                    }),
                );
                yield put(
                    appInit({
                        userId: response.data.user.id,
                        communityId: null,
                        token: response.data.user.access_token,
                    }),
                );

                // EMAIL VERIFICATION DISABLED - Always treat as verified
                // const isVerified = response.data.user.email_verified === true;
                const isVerified = true;

                // EMAIL VERIFICATION DISABLED - Skip verification redirect
                // if (!isVerified) {
                //     yield history.push("/verify-email");
                //     yield put(setAppLoading(false));
                //     return;
                // }
                if (!isItAWhiteLabel()) {
                    yield history.push("/communities");
                } else {
                    yield put(
                        joinCommunity({
                            id: whiteLabelConfig?.community?.id!,
                        }),
                    );
                    yield take(ECommunitiesActionTypes.joinCommunityCompleted);
                }
                yield put(setAppLoading(false));
            }
        } else {
            let errors = [];

            if (error?.response?.data?.errors) {
                errors = error.response.data.errors.reduce(
                    (acc: string[], cur: { errors: string[] }) => {
                        acc = acc.concat(
                            cur.errors.map((item: string) =>
                                item.toLowerCase(),
                            ),
                        );

                        return acc;
                    },
                    [],
                );
            }

            if (errors.indexOf("no active communities." as never) !== -1) {
                yield call(
                    notify,
                    "success",
                    "Authentication Success",
                    "Welcome back",
                );
                // EMAIL VERIFICATION DISABLED - Always treat as verified
                // const isVerified = error.response.data.user.email_verified === true;
                const isVerified = true;

                // EMAIL VERIFICATION DISABLED - Skip verification redirect
                // if (!isVerified) {
                //     updateToken(error.response.data.user.access_token);
                //     yield put(setUser({ credentials: error.response.data }));
                //     const customTokenValEncrypted = AES.encrypt(
                //         `${error.response.data.user.id}  ${error.response.data.user.access_token}`,
                //         CustomTokenPassword,
                //     );
                //     localStorage.setItem(LocalStorageKeyCustomToken, customTokenValEncrypted.toString());
                //     yield new Promise(resolve => setTimeout(resolve, 100));
                //     yield history.push("/verify-email");
                //     yield put(setAppLoading(false));
                //     return;
                // }

                // Proceed with full setup
                updateToken(error.response.data.user.access_token);
                yield put(
                    setUser({
                        credentials: error.response.data,
                    }),
                );

                // Only call appInit and navigate if user is verified
                yield put(
                    appInit({
                        userId: error.response.data.user.id,
                        communityId: null,
                        token: error.response.data.user.access_token,
                    }),
                );

                if (!error.response.data.user) {
                    yield history.push("/auth/sign-in");
                } else {
                    if (!isItAWhiteLabel()) {
                        yield history.push("/communities");
                    } else {
                        yield put(
                            joinCommunity({
                                id: whiteLabelConfig?.community?.id!,
                            }),
                        );
                        yield take(
                            ECommunitiesActionTypes.joinCommunityCompleted,
                        );
                    }
                }

                yield put(setAppLoading(false));
            } else {
                console.error({ error });
                yield call(
                    notify,
                    "error",
                    "Authentication Failed",
                    "Invalid credentials... please try again",
                );
                yield put(setAppLoading(false));
            }
        }
    }
}

function* singOutHandle({ payload }: any) {
    try {
        yield put(resetStore(payload));
        if (payload && payload.pathname) {
            const found = whitelist.reduce(
                (acc: boolean, cur: string) =>
                    acc || payload.pathname.indexOf(cur) === 0,
                false,
            );
            if (!found) yield history.push("/auth/sign-in");
        } else {
            yield history.push("/auth/sign-in");
        }
        clearToken();
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
    }
}

function* signUpHandle({ payload }: IAuthSignUpActionPayload) {
    yield put(setAuthenticationLoading(true));
    yield put(setAppLoading(true));
    try {
        const oneSignalId = yield select(getOneSignalId);
        const communityInvitation: ICommunityInvitation = yield select(
            getCommunityInvitation,
        );
        const userDetails = {
            user: {
                ...payload,
            },
            player_id: oneSignalId,
        };
        const { data } = yield call(api.auth.signUp, userDetails);

        if (!data.user.access_token || !data.user) {
            yield put(setAuthenticationLoading(false));
            yield put(setAppLoading(false));
            throw new Error("The user couldn't be created!");
        }

        updateToken(data.user.access_token);
        yield put(
            setUser({
                credentials: {
                    user: data.user,
                },
            }),
        );
        yield put(
            appInit({
                userId: data.user.id,
                communityId: "",
                token: data.user.access_token,
            }),
        );
        
        // EMAIL VERIFICATION DISABLED - Always treat as verified
        // const isVerified = data.user.email_verified === true;
        const isVerified = true;

        if (isItAWhiteLabel()) {
            yield put(
                joinCommunity({
                    id: whiteLabelConfig?.community?.id!,
                }),
            );
            yield take(ECommunitiesActionTypes.joinCommunityCompleted);
        } else if (communityInvitation) {
            yield put(joinCommunity({ id: communityInvitation.community.id }));
            yield take(ECommunitiesActionTypes.joinCommunityCompleted);
        // EMAIL VERIFICATION DISABLED - Skip verification redirect
        // } else if (!isVerified) {
        //     yield history.push("/verify-email");
        } else {
            yield history.push("/communities/all");
        }
        yield put(setAuthenticationLoading(false));
        yield put(setAppLoading(false));
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
        yield call(
            notify,
            "error",
            "Registration Failed",
            "An account with this email address already exists",
        );
        yield put(setAuthenticationLoading(false));
        yield put(setAppLoading(false));
    }
}

function* resetStoreHandle({ payload }: any) {
    try {
        yield put(setAppLoading(true));
        const user = yield select(getUser);
        if (user && user.id) yield call(api.auth.logout, { userId: user.id });
        clearToken();
        yield put(resetStoreSuccess());
        if (
            WHITELABEL_LOGIN_PAGE &&
            SSO_ENABLED
        ) {
            window.location.assign(
                WHITELABEL_LOGIN_PAGE,
            );
            return
        }
        if (payload && payload.pathname) {
            const found = whitelist.reduce(
                (acc: boolean, cur: string) =>
                    acc || payload.pathname.indexOf(cur) === 0,
                false,
            );
            if (!found) yield history.push("/auth/sign-in");
        } else {
            yield history.push("/auth/sign-in");
        }
        yield put(setAppLoading(false));
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
        clearToken();
        yield put(resetStoreSuccess());
        yield history.push("/auth/sign-in");
        yield put(setAppLoading(false));
    }
}

function* getInviteHandle({ payload }: ReturnType<typeof getInvite>) {
    const { token } = payload;
    try {
        const { data } = yield call(api.auth.getInvite, token);
        yield put(setInvite(data));
        yield history.push("/auth/sign-up");
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
        yield history.push("/auth/sign-up");
    }
}

function* getInvitationByCommunityLinkHandle({
    payload,
}: ReturnType<typeof getInvitationDataByCommunityLink>) {
    const { token } = payload;
    try {
        if (!token) throw new Error("Missing parameters!");
        const { data } = yield call(api.auth.communityPreviews, payload);
        yield put(
            setInvite({
                community: data,
            }),
        );
        yield history.push("/auth/sign-up");
    } catch (error) {
        rollbar.error(error)
        console.error({ error });
        yield history.push("/auth/sign-up");
    }
}

function* deleteAccountHandle() {
    try {
        yield put(setAuthenticationLoading(true));
        const user = yield select(getUser);
        const { data } = yield call(api.auth.deleteAccount, user.id);
        if (data?.user) {
            yield put(storeLogout());
        }
        yield put(setAuthenticationLoading(false));
        yield call(notify, "success", "User account successfully deleted!");
    } catch (error) {
        yield put(setAuthenticationLoading(false));
        yield call(notify, "error", "Failed to delete!");
    }
}

function* getUserCurrentSubscription({
    payload,
}: ReturnType<typeof getUserCurrentSubscriptionAction>) {
    const { communityId, userId } = payload;
    const globalUserId: number = yield select(getUserId);
    const loggedUserId = userId ? userId : globalUserId;
    try {
        if (!loggedUserId && !communityId) {
            throw new Error("Invalid parameters!");
        }
        // get and set the current subscription
        const { data: userProfileData } = yield call(
            api.profile.getUserProfile,
            {
                user_id: loggedUserId,
                community_id: communityId,
            },
        );

        const { data: editProfileData } = yield call(
            api.profile.getEditProfile,
            {
                user_id: loggedUserId,
                community_id: communityId,
            },
        );
        const { data: profileAccountSettingsData } = yield call(
            api.profile.getProfileAccountSettings,
            {
                user_id: loggedUserId,
                community_id: communityId,
            },
        );

        const userData = {
            user: {
                ...editProfileData.user,
                ...userProfileData.user,
                member_directory_opt_in:
                    profileAccountSettingsData.user.member_directory_opt_in,
                community_id: communityId,
                joined_community_id: communityId,
                currentPlanId: userProfileData.user.current_plan_id,
                currenSubscription: userProfileData.user.current_subscription,
            },
        };

        yield put(
            setUser({
                credentials: userData,
            }),
        );
        yield put(getSubscriptionManagementActionSaga({ communityId }));
        yield take(
            ESubscriptionActionTypes.COMPLETE_GET_SUBSCRIPTION_MANAGEMENT,
        );
    } catch (error) {
        rollbar.error(error)
        console.error(error);
    }

    yield put({ type: EAuthActionTypes.getUserCurrentSubscriptionCompleted });
}

// Email verification saga handlers
function* verifyEmailHandle({ payload }: any) {
	try {
		const { token } = payload;
		
		// Call the API to verify the email
		yield call(api.profile.verifyEmail, token);
		
		// Show success notification
		yield call(
			notify, 
			"success", 
			"Email Verified", 
			"Your email has been successfully verified."
		);
		
		// Dispatch completion action
		yield put(verifyEmailCompleted({ isVerified: true }));
	} catch (error: any) {
		console.error("Email verification failed:", error);
		
		// Check if user is already verified based on error response
		if (error?.response?.data?.message?.includes("already verified")) {
			yield call(
				notify, 
				"info", 
				"Already Verified", 
				"Your email has already been verified."
			);
			yield put(verifyEmailCompleted({ isVerified: true }));
		} else {
			yield call(
				notify, 
				"error", 
				"Verification Failed", 
				"The verification link is invalid or has expired."
			);
			yield put(verifyEmailFailed({ error }));
		}
		yield put(setAppLoading(false));
	}
}

function* resendVerificationEmailHandle() {
	try {
		yield put(setAppLoading(true));
		
		// Call the API to resend verification email
		yield call(api.profile.resendVerificationEmail);
		
		// Show success notification
		yield call(
			notify, 
			"success", 
			"Verification Email Sent", 
			"Please check your inbox for the verification link."
		);
		
		// Dispatch completion action
		yield put(resendVerificationEmailCompleted());
		yield put(setAppLoading(false));
	} catch (error) {
		console.error("Failed to resend verification email:", error);
		
		// Show error notification
		yield call(
			notify, 
			"error", 
			"Failed to Resend", 
			"There was a problem sending the verification email. Please try again."
		);
		
		yield put(resendVerificationEmailFailed({ error }));
		yield put(setAppLoading(false));
	}
}

function* checkEmailVerificationStatusHandle() {
	try {
		// const response = yield call(api.profile.checkEmailVerificationStatus);
		const isVerified = true;
		
		yield put(checkEmailVerificationStatusCompleted({ isVerified }));
	} catch (error) {
		console.error("Failed to check email verification status:", error);
		yield put(checkEmailVerificationStatusCompleted({ isVerified: false }));
	}
}

// function* joinCommunityHandle({
// 	payload,
// }: ReturnType<typeof joinCommunity>) {
// 	yield put(setAppLoading(true));
// 	const user = yield select(getUser);
// 	const communityInvitation: ICommunityInvitation = yield select(
// 		getCommunityInvitation,
// 	);

// 	try {
// 		if (!payload.id || !user?.id) throw new Error("Missing parameters!");

// 		// Continue with normal join flow if verified
// 		let params: any = {
// 			community_id: payload.id,
// 			user_id: user.id,
// 		};

// 		if (communityInvitation?.member_type) {
// 			params.invite_member_type = communityInvitation.member_type;
// 		}

// 		// Get community data
// 		const { data } = yield call(api.community.get, {
// 			community_id: payload.id,
// 		});

// 		const communityId = data.community?.id || payload.id;

// 		yield put(
// 			appInit({
// 				userId: user.id,
// 				communityId: communityId,
// 				token: user.access_token,
// 			}),
// 		);
//         alert("joinCommunityHandle 234");
// 		yield history.push("/dashboard");

// 		// Dispatch action to mark join as completed
// 		yield put({
// 			type: ECommunitiesActionTypes.joinCommunityCompleted
// 		});
// 	} catch (error: any) {
// 		console.error({ error });

// 		// Handle specific error cases
// 		if (
// 			error.response?.data?.errors?.[0]?.errors?.[0] ===
// 			"You are already a member of this community."
// 		) {
// 			const communityId = payload.id;
// 			yield put(
// 				appInit({
// 					userId: user?.id,
// 					communityId: communityId,
// 					token: user?.access_token,
// 				}),
// 			);
//             alert("joinCommunityHandle 123");
// 			yield history.push("/dashboard");
// 		} else {
// 			yield call(
// 				notify,
// 				"error",
// 				"Joining Failed",
// 				"There was an error joining the community",
// 			);
// 		}
// 	}
// 	yield put(setAppLoading(false));
// }

function* requestMagicLinkHandle({ payload }: ReturnType<typeof requestMagicLink>) {
	try {
		yield put(setAppLoading(true));
		yield call(api.auth.requestMagicLink, payload.email);
		
		yield call(
			notify,
			"success",
			"Magic Link Sent",
			"Please check your email for the magic link to login",
		);
		yield put(requestMagicLinkCompleted());
		yield put(setAppLoading(false));
		
		// If there's an onSuccess callback, call it
		if (payload.onSuccess && typeof payload.onSuccess === 'function') {
			yield call(payload.onSuccess);
		}
	} catch (error) {
		console.error({ error });
		
		// Check if it's a rate limit error (HTTP 429)
		if (error?.response?.status === 429) {
			yield call(
				notify,
				"error",
				"Rate Limit Exceeded",
				"Too many magic link requests. Please try again in 5 minutes.",
			);
		} else {
			// For other errors
			yield call(
				notify,
				"error",
				"Magic Link Request Failed",
				"We couldn't send a magic link to this email. Please try again later.",
			);
		}
		
		yield put(requestMagicLinkFailed({ error }));
		yield put(setAppLoading(false));
	}
}

function* verifyMagicLinkHandle({ payload }: ReturnType<typeof verifyMagicLink>) {
	try {
		yield put(setAppLoading(true));
		const { data } = yield call(api.auth.verifyMagicLink, payload.token);
		
		if (!data.user.access_token || !data.user) {
			yield call(
				notify,
				"error",
				"Authentication Failed",
				"Invalid magic link... please try again",
			);
			yield put(setAppLoading(false));
            yield history.push("/auth/sign-in");
			return;
		}

		yield call(
			notify,
			"success",
			"Authentication Success",
			"Welcome back",
		);
		updateToken(data.user.access_token);


		// const userData = yield select(getUser);
		yield put(getCommunitiesJoined({ q: ""}));

        const { data:dataCommunitiesJoined } = yield call(api.communitiesJoined.get,  { page: 1, q: "" });
        const hasJoinedProcess = dataCommunitiesJoined?.communities?.length > 0;

		// Magic link authentication automatically verifies the email
		yield put(checkEmailVerificationStatusCompleted({ isVerified: true }));

		yield put(
			setUser({
				credentials: data,
			}),
		);

		if (!data.user) {
			yield put(
				appInit({
					userId: data.user.id,
					communityId: "",
					token: data.user.access_token,
				}),
			);
			yield history.push("/communities/all");
			yield put(verifyMagicLinkCompleted({ user: data.user }));
			yield put(setAppLoading(false));
			return;
		}

		let communityId: number = 0;
		let communityData: Record<string, any> | null = null;
		
		communityId =
			data.user.last_visited_community_id ||
			data.user.joined_community_id ||
			data.user.last_previewed_community_id;

        if(!communityId){
            if(hasJoinedProcess){
                // alert("hasJoinedProcess");
                console.log(hasJoinedProcess);
                yield put(setAppLoading(false));
                yield history.push(`/communities`);
                return
            } 
        }

		if (communityId) {
			try {
				const { data: homeResponse } = yield call(api.community.home, {
					community_id: communityId,
				});
				communityData = homeResponse;
				
				// Debug logging
				console.log("Magic Link - Community Data:", communityData);
				console.log("Magic Link - Access Status:", communityData?.meta?.access_status);
			} catch (error) {
				console.error("Error fetching community data:", error);
				// If can't fetch community data, redirect to communities
				yield put(
					appInit({
						userId: data.user.id,
						communityId: "",
						token: data.user.access_token,
					}),
				);
				yield history.push("/communities/all");
				yield put(verifyMagicLinkCompleted({ user: data.user }));
				yield put(setAppLoading(false));
				return;
			}
		}

		// Check for subscription_inactive access_status first
		if (
			communityData &&
			communityData?.meta?.access_status === "subscription_inactive"
		) {
			console.log("Magic Link - Detected subscription_inactive, redirecting to communities/all");
			// If subscription is inactive, redirect to all communities page
			yield put(
				appInit({
					userId: data.user.id,
					communityId: "",
					token: data.user.access_token,
				}),
			);
			console.log("Magic Link - About to navigate to /communities/all");
			yield history.push("/communities/all");
			console.log("Magic Link - Navigation completed");
			yield put(verifyMagicLinkCompleted({ user: data.user }));
			yield put(setAppLoading(false));
			return;
		}

		if (
			communityData &&
			(communityData?.meta?.privacy === "open" ||
				communityData?.meta?.is_approved)
		) {
			yield put(
				getEditProfile({ communityId, userId: data.user.id }),
			);
			yield put(
				getUserCurrentSubscriptionAction({
					communityId,
					userId: data.user.id,
				}),
			);
			yield put(
				appInit({
					userId: data.user.id,
					communityId: communityId,
					token: data.user.access_token,
				}),
			);
			yield put(
				getSubscriptionManagementActionSaga({
					communityId,
				}),
			);
			yield take(
				ESubscriptionActionTypes.COMPLETE_GET_SUBSCRIPTION_MANAGEMENT,
			);
		} else {
			// No community data or not approved, redirect to communities
			yield put(
				appInit({
					userId: data.user.id,
					communityId: "",
					token: data.user.access_token,
				}),
			);
			yield history.push("/communities/all");
		}
		
		yield put(verifyMagicLinkCompleted({ user: data.user }));
		yield put(setAppLoading(false));
	} catch (error) {
		console.error({ error });
		yield call(
			notify,
			"error",
			"Authentication Failed",
			"Invalid magic link... please try again",
		);
		yield put(verifyMagicLinkFailed({ error }));
		yield put(setAppLoading(false));
        yield history.push("/auth/sign-in");
	}
}

function* navigateToDashboardHandle() {
	try {
		yield put(setAppLoading(true));
		const user = yield select(getUser);
		
		if (!user) {
			// If no user is logged in, redirect to sign-in
			yield history.push("/auth/sign-in");
			yield put(setAppLoading(false));
			return;
		}
		// Use user data from Redux store to determine community
		let communityId: number = 0;
		communityId =
			user.last_visited_community_id ||
			user.joined_community_id ||
			user.last_previewed_community_id;

		if (communityId) {
			try {
				// Check if user has access to the community
				const { data: communityData } = yield call(api.community.home, {
					community_id: communityId,
				});
				
				// Check for subscription_inactive access_status first
				if (communityData.meta.access_status === "subscription_inactive") {
					// If subscription is inactive, redirect to all communities page
					yield put(
						appInit({
							userId: user.id,
							communityId: "",
							token: user.access_token,
						}),
					);
					
					if (!isItAWhiteLabel()) {
						yield history.push("/communities");
					} else {
						yield put(
							joinCommunity({
								id: whiteLabelConfig?.community?.id!,
							}),
						);
						yield take(
							ECommunitiesActionTypes.joinCommunityCompleted,
						);
					}
				} else if (
					communityData.meta.privacy === "open" ||
					communityData.meta.is_approved
				) {
					// User has access to community - initialize app and redirect to dashboard
					yield put(
						appInit({
							userId: user.id,
							communityId: communityId,
							token: user.access_token,
						}),
					);
					
					// Navigate to dashboard
					yield history.push("/dashboard");
				} else {
					// User doesn't have access to the community, redirect to communities
					yield put(
						appInit({
							userId: user.id,
							communityId: "",
							token: user.access_token,
						}),
					);
					
					if (!isItAWhiteLabel()) {
						yield history.push("/communities");
					} else {
						yield put(
							joinCommunity({
								id: whiteLabelConfig?.community?.id!,
							}),
						);
						yield take(
							ECommunitiesActionTypes.joinCommunityCompleted,
						);
					}
				}
			} catch (error) {
				// If community access check fails, redirect to communities
				yield put(
					appInit({
						userId: user.id,
						communityId: "",
						token: user.access_token,
					}),
				);
				
				if (!isItAWhiteLabel()) {
					yield history.push("/communities");
				} else {
					yield put(
						joinCommunity({
							id: whiteLabelConfig?.community?.id!,
						}),
					);
					yield take(
						ECommunitiesActionTypes.joinCommunityCompleted,
						);
				}
			}
		} else {
			// No community found, redirect to communities page
			yield put(
				appInit({
					userId: user.id,
					communityId: "",
					token: user.access_token,
				}),
			);
			
			if (!isItAWhiteLabel()) {
				yield history.push("/communities/all");
			} else {
				yield put(
					joinCommunity({
						id: whiteLabelConfig?.community?.id!,
					}),
				);
				yield take(
					ECommunitiesActionTypes.joinCommunityCompleted,
				);
			}
		}
		
		yield put(setAppLoading(false));
	} catch (error) {
		rollbar.error(error);
		console.error({ error });
		yield call(
			notify,
			"error",
			"Navigation Failed",
			"There was an error navigating to dashboard",
		);
		
		// Fallback to communities page
		yield history.push("/communities");
		yield put(setAppLoading(false));
	}
}

export default function* rootSaga() {
	yield all([
		takeEvery(EAuthActionTypes.authByToken, authByTokenHandle),
		takeEvery(EAuthActionTypes.authInit, loginHandle),
		takeEvery(EAuthActionTypes.authSignOut, singOutHandle),
		takeEvery(EAuthActionTypes.authSignUp, signUpHandle),
		takeEvery(EAppActionTypes.resetStore, resetStoreHandle),
		takeEvery(EAuthActionTypes.getInvite, getInviteHandle),
		takeEvery(EAuthActionTypes.deleteAccount, deleteAccountHandle),
		takeEvery(
			EAuthActionTypes.getUserCurrentSubscription,
			getUserCurrentSubscription,
		),
		takeEvery(
			EAuthActionTypes.getInvitationDataByCommunityLink,
			getInvitationByCommunityLinkHandle,
		),
		takeEvery(EAuthActionTypes.verifyEmail, verifyEmailHandle),
		takeEvery(EAuthActionTypes.resendVerificationEmail, resendVerificationEmailHandle),
		takeEvery(EAuthActionTypes.checkEmailVerificationStatus, checkEmailVerificationStatusHandle),
		// takeEvery(ECommunitiesActionTypes.joinCommunity, joinCommunityHandle),
		takeEvery(EAuthActionTypes.requestMagicLink, requestMagicLinkHandle),
		takeEvery(EAuthActionTypes.verifyMagicLink, verifyMagicLinkHandle),
		takeEvery(EAuthActionTypes.navigateToDashboard, navigateToDashboardHandle),
	]);
}
